import React from 'react';
import { Box, Button, Container, Divider, Grid, Link, Typography } from '@mui/material';
import { WhatsApp } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const PRODUCT_LINKS = [
  { label: 'Features', href: '#features' },
  { label: 'How it works', href: '#how-it-works' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'FAQ', href: '#faq' },
];

const COMPANY_LINKS = [
  { label: 'About', href: '/#features' },
  { label: 'Contact', href: 'mailto:support@bookmylibrary.in' },
  { label: 'Careers', href: '#' },
  { label: 'Press', href: '#' },
];

const LEGAL_LINKS = [
  { label: 'Privacy', href: '/privacy' },
  { label: 'Terms', href: '/terms' },
  { label: 'Refunds', href: '/refunds' },
  {
    label: 'Attribution',
    href: '/attribution',
    title: 'IP geolocation by DB-IP, licensed under CC BY 4.0',
  },
];

function FooterLinkColumn({ heading, links }) {
  return (
    <Box>
      <Typography
        variant="overline"
        sx={{
          color: 'rgba(255,255,255,0.5)',
          fontWeight: 700,
          letterSpacing: '0.1em',
          fontSize: '0.7rem',
          display: 'block',
          mb: 1.5,
        }}
      >
        {heading}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {links.map(({ label, href, title }) => (
          <Link
            key={label}
            href={href}
            title={title}
            underline="none"
            sx={{
              color: 'rgba(255,255,255,0.75)',
              fontSize: '0.875rem',
              transition: 'color 0.15s ease',
              '&:hover': { color: '#F26522' },
            }}
          >
            {label}
          </Link>
        ))}
      </Box>
    </Box>
  );
}

function MarketingFooter() {
  const theme = useTheme();
  const orange = theme.palette.brand.orange.main;

  return (
    <Box
      component="footer"
      sx={{
        background: '#0B1A33',
        color: '#fff',
        pt: { xs: 7, md: 10 },
        pb: { xs: 4, md: 6 },
      }}
    >
      <Container maxWidth="lg">
        {/* Top row */}
        <Grid container spacing={{ xs: 5, md: 6 }}>
          {/* Brand + tagline */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box
                component="img"
                src="/logo192.png"
                alt="BookMyLibrary logo"
                sx={{ width: 36, height: 36, borderRadius: '8px' }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  fontSize: '1.15rem',
                  background: `linear-gradient(135deg, ${orange}, ${theme.palette.brand.golden.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                BookMyLibrary
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.75, maxWidth: 300 }}
            >
              India's fastest-growing study library platform. Find a seat. Start studying.
            </Typography>
          </Grid>

          {/* Link columns */}
          <Grid item xs={6} sm={4} md={2}>
            <FooterLinkColumn heading="Product" links={PRODUCT_LINKS} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <FooterLinkColumn heading="Company" links={COMPANY_LINKS} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <FooterLinkColumn heading="Legal" links={LEGAL_LINKS} />
          </Grid>

          {/* Contact */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography
              variant="overline"
              sx={{
                color: 'rgba(255,255,255,0.5)',
                fontWeight: 700,
                letterSpacing: '0.1em',
                fontSize: '0.7rem',
                display: 'block',
                mb: 1.5,
              }}
            >
              Contact
            </Typography>
            <Link
              href="mailto:support@bookmylibrary.in"
              underline="none"
              sx={{
                display: 'block',
                color: 'rgba(255,255,255,0.75)',
                fontSize: '0.875rem',
                mb: 1,
                '&:hover': { color: orange },
                transition: 'color 0.15s ease',
              }}
            >
              support@bookmylibrary.in
            </Link>
            <Link
              href="tel:+918595675155"
              underline="none"
              sx={{
                display: 'block',
                color: 'rgba(255,255,255,0.75)',
                fontSize: '0.875rem',
                mb: 2,
                '&:hover': { color: orange },
                transition: 'color 0.15s ease',
              }}
            >
              +91 85956 75155
            </Link>
            <Button
              variant="outlined"
              component="a"
              href="https://wa.me/918595675155"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<WhatsApp />}
              size="small"
              sx={{
                borderColor: '#25D366',
                color: '#25D366',
                fontWeight: 700,
                borderWidth: 1.5,
                '&:hover': {
                  borderColor: '#1DA851',
                  color: '#1DA851',
                  background: 'rgba(37,211,102,0.08)',
                  borderWidth: 1.5,
                },
              }}
            >
              WhatsApp
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 5 }} />

        {/* Bottom bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>
            © 2026 BookMyLibrary. All rights reserved.
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem' }}>
            IP geolocation by{' '}
            <Link
              href="https://db-ip.com"
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{ color: 'rgba(255,255,255,0.4)' }}
            >
              DB-IP
            </Link>{' '}
            (CC BY 4.0)
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default MarketingFooter;
